import type React from 'react';
import { useContext, useEffect, useState } from 'react';
import { Form, Toast, useToast } from '@zillow/constellation-10';
import { SpinnerContainer } from '@zg-rentals/rental-manager-env';
import type { TimeZoneIds } from '@zg-rentals/touring';

import { DISPLAY_SERVER_ERROR_AT } from '../../../constants/WizardConstants';
import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import type { PageWizardProps } from '../../Wizard/PageWizard';
import PageWizard from '../../Wizard/PageWizard';
import WizardAlert from '../../WizardAlert';
import TourSchedulingBookingMethodBody from './components/TourSchedulingBookingMethodBody';
import { useTourSchedulingContext } from './context';
import { WizardContext } from '../../Wizard/context';
import { useSelector } from '../../../selectors/useSelector';
import accessUtils from '../../../utils/accessUtils';

import { WizardFlexContainer } from '../../../styles/wizard';
import { ActionMetadataEntryPoint } from '@zg-rentals/ts-z-calendar-client';

export type TourSchedulingBookingMethodProps = {
  onGoToPrevStep: () => void;
  onGoToNextStep: () => void;
  onExitWizard: () => void;
  wizardTitle: React.ReactNode;
  progressStepper: React.ReactNode;
  totalSteps: number;
  currentStepIndex: number;
  showDesktopViewBreakpoint: string;
} & PageWizardProps;

const TourSchedulingBookingMethod = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: TourSchedulingBookingMethodProps) => {
  const { enqueueToast } = useToast();
  const {
    // @ts-expect-error WizardContext
    wizardState: { isFetchingData },
  } = useContext(WizardContext);
  const {
    modularListingState: { serverErrorObj },
  } = useContext(ModularListingContext);
  const { canBeRelisted } = useSelector((state) => ({ canBeRelisted: state.properties.currentProperty.canBeRelisted }));
  const canAccessRelistingFlow = accessUtils.canAccessRelistingFlow({ canBeRelisted });

  const [clickedNext, setClickedNext] = useState(false);
  const [clickedBackNext, setClickedBack] = useState(false);

  const { selectedBookingMethod, setSelectedBookingMethod, tourCalendarApiHook } = useTourSchedulingContext();
  const { isDisableTouringForListingLoading } = tourCalendarApiHook;

  useEffect(() => {
    const { fieldName } = serverErrorObj;

    if (!fieldName && !isFetchingData) {
      checkNavToStep();
    }
  }, [isFetchingData]);

  const checkNavToStep = () => {
    if (clickedNext) {
      onNextClick();
    }

    if (clickedBackNext) {
      onBackClick();
    }
  };

  const onNextClick = async () => {
    if (isFetchingData) {
      setClickedNext(true);
      return;
    }

    try {
      if (!selectedBookingMethod && tourCalendarApiHook.isTouringSchedulingEnabled) {
        await tourCalendarApiHook.disableTouringForListing();
      }

      // When zoneIdWithoutTourConfig exists, the listing still has its original timezone stored
      // (touring was auto-disabled after 14+ days inactive), so skip the timezone step
      if (
        canAccessRelistingFlow &&
        selectedBookingMethod &&
        tourCalendarApiHook.initialZoneIdWithoutTourConfig &&
        selectedBookingMethod !== tourCalendarApiHook.tourConfirmationType
      ) {
        await tourCalendarApiHook.enableTouringForListing(
          selectedBookingMethod,
          tourCalendarApiHook.initialZoneIdWithoutTourConfig as TimeZoneIds,
          ActionMetadataEntryPoint.REACTIVATION_LISTING,
        );
      }

      onGoToNextStep();
    } catch (error) {
      enqueueToast(
        <Toast tone="critical">Your tour setting changes couldn&apos;t be saved. Try again in a few moments.</Toast>,
      );
    }
  };

  const onBackClick = () => {
    if (isFetchingData) {
      setClickedBack(true);
      return;
    }

    onGoToPrevStep();
  };

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onNextClick}
      onBackClick={onBackClick}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
      isNextDisabled={isDisableTouringForListingLoading}
    >
      <div data-testid="TourSchedulingBookingMethod">
        {tourCalendarApiHook.isGetTourConfigAndAvailabilityLoading ? (
          <SpinnerContainer />
        ) : (
          <>
            <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />

            <WizardFlexContainer>
              <Form asChild onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}>
                <TourSchedulingBookingMethodBody
                  selectedBookingMethod={selectedBookingMethod}
                  onSelectBookingMethod={setSelectedBookingMethod}
                />
              </Form>
            </WizardFlexContainer>
          </>
        )}
      </div>
    </PageWizard>
  );
};

export default TourSchedulingBookingMethod;
